import React, { useEffect, useRef, useMemo } from "react";
import { RigidBody } from "@react-three/rapier";
import { useUI } from "../../stores/useUI";
import * as THREE from "three";
import { useGame } from "../../../src/stores/useGame";

interface FloorProps {
  size: [number, number, number];
  position: [number, number, number];
  mapId: string;
  colorInner: string;
  colorOuter: string;
  innerColorPercent: number;
}

export default function Floor({ size, position, colorInner, colorOuter, innerColorPercent }: FloorProps) {
  const boundaryRadius = Math.sqrt(innerColorPercent / 100) * 0.5;
  const markItemLoaded = useUI((state) => state.markItemLoaded);
  const markedAsLoaded = useRef(false);
  const moveToPoint = useGame((state) => state.moveToPoint);
  const setMoveToPoint = useGame((state) => state.setMoveToPoint);
  const isMouseDownForMoveToPoint = useGame((state) => state.isMouseDownForMoveToPoint);
  const setIsMouseDownForMoveToPoint = useGame((state) => state.setIsMouseDownForMoveToPoint);

  useEffect(() => {
    if (!isMouseDownForMoveToPoint) {
      setMoveToPoint(null);
    }
  }, [moveToPoint]);

  useEffect(() => {
    if (!markedAsLoaded.current) {
      markItemLoaded();
      markedAsLoaded.current = true;
    }
  }, []);

  const floorMaterial = useMemo(() => {
    const mat = new THREE.MeshStandardMaterial({ roughness: 1, metalness: 0 });
    const inner = new THREE.Color(colorInner);
    const outer = new THREE.Color(colorOuter);

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.colorInner = { value: inner };
      shader.uniforms.colorOuter = { value: outer };
      shader.uniforms.boundaryRadius = { value: boundaryRadius };

      // Pass local XZ position as varying
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `
        #include <common>
        varying vec2 vFloorPos;
        `
      );
      shader.vertexShader = shader.vertexShader.replace(
        '#include <project_vertex>',
        `
        #include <project_vertex>
        vFloorPos = position.xz / vec2(${size[0]}.0, ${size[2]}.0);
        `
      );

      // Declare uniforms, varying and helper function
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `
        #include <common>
        varying vec2 vFloorPos;
        uniform vec3 colorInner;
        uniform vec3 colorOuter;
        uniform float boundaryRadius;

        float floorHash(float n) {
          return fract(sin(n) * 43758.5453123);
        }
        `
      );

      // Two-color square boundary with V-shaped zigzag
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <color_fragment>',
        `
        #include <color_fragment>

        vec2 p = vFloorPos;

        // Square (Chebyshev) distance — equal along square perimeter
        float sd = max(abs(p.x), abs(p.y));

        // Perimeter parameter 0-1 going clockwise around the square
        float ax = abs(p.x), ay = abs(p.y);
        float perimT;
        if (ax > ay) {
          if (p.x > 0.0) perimT = (p.y + 0.5) / 4.0;
          else            perimT = (2.5 - p.y) / 4.0;
        } else {
          if (p.y > 0.0) perimT = (1.5 - p.x) / 4.0;
          else            perimT = (p.x + 3.5) / 4.0;
        }

        // V-shaped zigzag along the square perimeter
        float nTeeth = 48.0;
        float tooth = floor(perimT * nTeeth);
        float t = fract(perimT * nTeeth);
        float wave = 1.0 - abs(t * 2.0 - 1.0);
        float peakAmp = floorHash(tooth) * 0.06 + 0.02;
        float boundary = boundaryRadius + wave * peakAmp;

        float aa = fwidth(sd) * 1.5;
        float mask = smoothstep(boundary - aa, boundary + aa, sd);

        diffuseColor.rgb = mix(colorInner, colorOuter, mask);
        `
      );
    };

    return mat;
  }, [colorInner, colorOuter, boundaryRadius, size]);

  return (
    <group>
      <RigidBody type="fixed">
        <mesh
          receiveShadow
          castShadow
          position={position}
          material={floorMaterial}
          onPointerDown={({ point }) => {
            setIsMouseDownForMoveToPoint(true);
            setMoveToPoint(point);
          }}
          onPointerMove={({ point }) => {
            if (isMouseDownForMoveToPoint) {
              setMoveToPoint(point);
            }
          }}
          onPointerUp={() => {
            setIsMouseDownForMoveToPoint(false);
            setMoveToPoint(null);
          }}
          onPointerLeave={() => {
            setIsMouseDownForMoveToPoint(false);
            if (isMouseDownForMoveToPoint) {
              setMoveToPoint(null);
            }
          }}
        >
          <boxGeometry args={size} />
        </mesh>
      </RigidBody>
    </group>
  );
}
